import React from 'react';
import { colors } from '../../styles/tokens';

export interface SelectAllHeaderCellProps {
  // Row selection mode props
  rowSelectionMode: boolean;
  allRowsSelected: boolean;
  someRowsSelected: boolean;
  // Cell selection mode props
  selectionSize: number;
  // Event handler
  onClick: () => void;
}

/**
 * Header cell for the row number column with select-all functionality
 * Supports both row selection mode (checkboxes) and cell selection mode
 */
export const SelectAllHeaderCell: React.FC<SelectAllHeaderCellProps> = ({
  rowSelectionMode,
  allRowsSelected,
  someRowsSelected,
  selectionSize,
  onClick,
}) => {
  const hasAnySelection = rowSelectionMode ? someRowsSelected : selectionSize > 0;

  return (
    <div
      className="flex items-center justify-center text-[10px] text-neutral-400 font-medium select-none cursor-pointer group/selectall hover:bg-neutral-100"
      style={{
        width: '40px',
        minWidth: '40px',
        maxWidth: '40px',
        position: 'sticky',
        left: 0,
        zIndex: 32,
        backgroundColor: hasAnySelection ? colors.primary[50] : colors.neutral[50],
        borderRight: `1px solid ${colors.neutral[200]}`,
        transition: 'background-color 100ms ease-out'
      }}
      onClick={onClick}
      title={rowSelectionMode ? (allRowsSelected ? 'Deselect all' : 'Select all') : 'Select all'}
    >
      {rowSelectionMode ? (
        // Always show checkbox in row selection mode
        <div
          className="flex w-4 h-4 rounded border-2 items-center justify-center transition-colors"
          style={{
            borderColor: hasAnySelection ? colors.primary[500] : colors.neutral[300],
            backgroundColor: allRowsSelected ? colors.primary[500] : 'transparent'
          }}
        >
          {allRowsSelected ? (
            <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : someRowsSelected ? (
            // Partial selection indicator (dash)
            <div className="w-2 h-0.5 bg-primary-500 rounded" />
          ) : null}
        </div>
      ) : (
        <>
          {/* Show checkbox on hover, # otherwise */}
          <span className="group-hover/selectall:hidden tracking-wider">#</span>
          <div
            className="hidden group-hover/selectall:flex w-4 h-4 rounded border-2 items-center justify-center transition-colors"
            style={{
              borderColor: selectionSize > 0 ? colors.primary[500] : colors.neutral[300],
              backgroundColor: selectionSize > 0 ? colors.primary[500] : 'transparent'
            }}
          >
            {selectionSize > 0 && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </>
      )}
    </div>
  );
};

SelectAllHeaderCell.displayName = 'SelectAllHeaderCell';
